/**
 * /admin/portfolios — client portfolios from the custodian's daily positions file (HP).
 *
 * Pedro 2026-10-18: "a new card in Admin called Portfolios. One tab will allow the upload.
 * One other will allow the user to see each portfolio, requesting it by the 6 rightmost
 * digits in "Portfolio" number as in appears in the uploaded file."
 *
 * Upload: choose the bank (whose file it is; Banks arc step 2, 0113: a portfolio is bank +
 * number), the positions date and the file → the preview says what the file holds
 * and what saving does (portfolios, positions, securities not in the Securities Master,
 * lines without an ISIN, portfolios already stored for that date) → Save positions (a
 * replacement asks first). Securities the master lacks are kept in their portfolios and
 * added to the master as non-classified (Pedro), so they can be classified in
 * Admin → Securities.
 *
 * View portfolio (Pedro 2026-10-18, second round): a portfolio picker (the PersonPicker
 * pattern); a stored date; any saved classification VIEW (default the active one) whose
 * lenses show one column per tier, as in Securities; Group by a lens or no grouping; the
 * Securities grid's "Columns" picker (shared ColumnsPicker) and "Freeze columns through".
 * Classification is read live, so classifying a security changes every stored date at once.
 *
 * View security: a held security → its line in every portfolio holding it on a date, each
 * portfolio opening in View portfolio.
 *
 * Gated by `portfolios.manage` (migration 0107, admin-only). Design:
 * `_MEMORY/decisions/2026-10-17_client-portfolios-holdings-design.md`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Anchor, Badge, Button, Card, Container, Group, Loader, Modal, Select, Stack,
  Table, Tabs, Text, TextInput, Title, Tooltip, type ComboboxItem, type OptionsFilter,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAdjustmentsHorizontal, IconAlertTriangle, IconInfoCircle, IconArrowBackUp, IconArrowsExchange, IconBriefcase, IconBuildingBank, IconCheck, IconLink,
  IconReceiptOff, IconSettings, IconUpload,
} from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';

import { banksApi } from '../api/banks';
import { ApiError } from '../api/client';
import { portfoliosApi, type HeldSecurity, type UploadReport } from '../api/portfolios';
import { securitiesApi } from '../api/securities';
import { BackToCompliance } from '../components/BackToCompliance';
import { EntityPicker } from '../components/EntityPicker';
import { PortfolioNoCostTab } from '../components/PortfolioNoCostTab';
import { PortfolioTradesTab } from '../components/PortfolioTradesTab';
import { RateNotices, sourceWhy } from '../components/RateNotices';
import { DisabledTooltipButton } from '../components/DisabledTooltipButton';
import { useDirtyForm } from '../components/DirtyFormProvider';
import { TabDirtyCtx } from '../components/TabDirty';
import { RequireAccess } from '../components/RequireAccess';
import { XlsxDropzone } from '../components/XlsxDropzone';
import { useCurrentUser } from '../hooks/useAuth';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { usePermissions } from '../hooks/usePermissions';
import { AssignTab } from './PortfolioAssignTab';
import { SetupTab } from './PortfolioSetupTab';
import { TradeRulesTab } from './PortfolioTradeRulesTab';
import { ViewTab, type ViewRequest } from './PortfolioViewTab';
import type { TradeRun } from '../api/trades';
import { portfolioLayoutsApi } from '../api/portfolioLayouts';
import { localTodayISO } from '../utils/localDate';
import { costUnknown } from '../utils/portfolioConsolidation';

const errMsg = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong.');
const eur = (s: string | null | undefined) =>
  s == null ? '—' : Number(s).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const num = (s: string | null | undefined, max = 6) =>
  s == null ? '—' : Number(s).toLocaleString(undefined, { maximumFractionDigits: max });
const pct = (s: string | null | undefined) => (s == null ? '—' : `${Number(s).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`);
const NON_CLASSIFIED = 'Non-Classified Securities';
/** The tabs that hold Edit forms (Save changes / Discard): leaving one with unsaved changes asks. */
const SETUP_TABS = new Set(['assign', 'rules', 'setup']);

export function PortfoliosPage() {
  useDocumentTitle('Portfolios');
  // HP-R (Pedro 2026-10-20): RM / Compliance read portfolios (`portfolios.read`) from a
  // Welcome-page card; only `portfolios.manage` (Admin) sees Upload positions.
  const { can, isLoading: permsLoading } = usePermissions();
  const canUpload = can('portfolios.manage');
  // TRADE-CONS 1a / 1b: consolidations and models live on Assign to…, so a holder of
  // `portfolios.consolidate` or `portfolios.models` sees that tab too (each column keeps its own permission).
  const canAssign = canUpload || can('portfolios.consolidate') || can('portfolios.models');
  const canRules = can('portfolios.trade_rules');
  const { confirmDiscardAsync } = useDirtyForm();
  const tabDirty = useRef<Set<string>>(new Set());
  const reportTabDirty = useCallback((id: string, d: boolean) => {
    if (d) tabDirty.current.add(id); else tabDirty.current.delete(id);
  }, []);
  const { user } = useCurrentUser();
  // ?tab=view opens on View portfolio (the old Trades address leads here, TRADES-RM).
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<string | null>(searchParams.get('tab'));
  // COST-FIX: a link to ?tab=… from inside the page (View portfolio's No cost banner) switches tab.
  const tabParam = searchParams.get('tab');
  useEffect(() => { if (tabParam) setTab(tabParam); }, [tabParam]);
  // TAB-ORDER (Pedro 2026-10-25): View and trade first, so it opens by default for everyone.
  const shownTab = tab ?? 'view';
  // A portfolio is handed to View portfolio by its ref (bank + number, 0113); TRADE-LIST: with a
  // draft id, that draft opens in trade mode (a Trades row here, or an agreement's Trades row via
  // ?portfolio=<ref>&draft=<id>).
  const [viewRequest, setViewRequest] = useState<ViewRequest>(() => ({
    ref: searchParams.get('portfolio') ?? '', n: searchParams.get('portfolio') ? 1 : 0, draft: searchParams.get('draft') }));
  const openPortfolio = (ref: string, draft?: string | null) => {
    setViewRequest((r) => ({ ref, n: r.n + 1, draft: draft ?? null })); setTab('view');
  };
  // TRADE-CONS 4b: a run opens on its consolidation, in trade mode.
  const openRun = (run: TradeRun) => {
    setViewRequest((r) => ({ ref: '', n: r.n + 1, draft: null,
      run: { id: run.id, consolidationId: run.consolidation_id, refs: run.drafts.map((d) => d.portfolio) } }));
    setTab('view');
  };
  // 5a smoke (Pedro 2026-11-01): leaving a set-up tab with unsaved changes for another tab of this
  // page asks first, as leaving the page does; those tabs are rendered only while shown, so
  // Discard really discards (TabDirty).
  const changeTab = (v: string | null) => {
    if (!v || v === shownTab) return;
    if (SETUP_TABS.has(shownTab) && tabDirty.current.size > 0) {
      void confirmDiscardAsync().then((ok) => { if (ok) { tabDirty.current.clear(); setTab(v); } });
    } else setTab(v);
  };
  if (permsLoading) return null;
  return (
    <RequireAccess permission="portfolios.read">
      {/* Full width like Securities, and a one-line header, so View portfolio gets the
          height for its rows (Pedro 2026-10-19, HP-V). */}
      <Container size="xl" fluid>
        <Stack gap={4}>
          <Group gap="md" align="center" wrap="nowrap">
            {/* N1 (Pedro 2026-10-25): back to where the user came from (the Welcome or the Admin
                card, `?from=`); without it, by role as before. */}
            {(searchParams.get('from') ?? (user?.role === 'admin' ? 'admin' : 'welcome')) === 'admin'
              ? <BackToCompliance to="/admin" label="Back to Admin" />
              : <BackToCompliance to="/" label="Back to Welcome" />}
            <Tooltip withArrow label="Client portfolios from the custodian's daily positions file, grouped by the classifications set in Admin → Securities.">
              <Title order={3}>Portfolios</Title>
            </Tooltip>
          </Group>
          <TabDirtyCtx.Provider value={reportTabDirty}>
          <Tabs value={shownTab} onChange={changeTab} keepMounted>
            {/* TAB-ORDER (Pedro 2026-10-25): View and trade, Security, Upload, Assign, Setup. */}
            <Tabs.List>
              {/* Pedro 2026-10-23: "rename the View portfolio 'View and trade portfolio'" — for
                  whoever can trade (`trades.draft`); others keep "View portfolio" (no Trade button). */}
              <Tabs.Tab value="view" leftSection={<IconBriefcase size={14} />}>
                {can('trades.draft') ? 'View and trade portfolio' : 'View portfolio'}
              </Tabs.Tab>
              <Tabs.Tab value="security" leftSection={<IconBuildingBank size={14} />}>View security</Tabs.Tab>
              {/* TRADE-LIST (Pedro 2026-10-25): every portfolio's trade drafts; read by every
                  Portfolios reader, ticked by holders of `trades.draft`. */}
              <Tabs.Tab value="trades" leftSection={<IconArrowsExchange size={14} />}>Trades</Tabs.Tab>
              {/* COST-FIX (Pedro 2026-10-27): every security line without cost information. */}
              <Tabs.Tab value="nocost" leftSection={<IconReceiptOff size={14} />}>No cost</Tabs.Tab>
              {canUpload && <Tabs.Tab value="upload" leftSection={<IconUpload size={14} />}>Upload positions</Tabs.Tab>}
              {canAssign && <Tabs.Tab value="assign" leftSection={<IconLink size={14} />}>Assign to…</Tabs.Tab>}
              {/* TRADE-CONS 5a (Pedro O4): each consolidation's trade rules, `portfolios.trade_rules` (F2). */}
              {canRules && <Tabs.Tab value="rules" leftSection={<IconAdjustmentsHorizontal size={14} />}>Assign trade rules</Tabs.Tab>}
              {canUpload && <Tabs.Tab value="setup" leftSection={<IconSettings size={14} />}>Setup upload</Tabs.Tab>}
            </Tabs.List>
            {canUpload && <Tabs.Panel value="upload" pt="md"><UploadTab onOpenPortfolio={openPortfolio} /></Tabs.Panel>}
            {canAssign && <Tabs.Panel value="assign" pt="md">{shownTab === 'assign' && <AssignTab onOpenPortfolio={openPortfolio} />}</Tabs.Panel>}
            {canRules && <Tabs.Panel value="rules" pt="md">{shownTab === 'rules' && <TradeRulesTab />}</Tabs.Panel>}
            {canUpload && <Tabs.Panel value="setup" pt="md">{shownTab === 'setup' && <SetupTab />}</Tabs.Panel>}
            {/* TRADES-RM (Pedro 2026-10-23): "simply a button in the View portfolio tab", for
                whoever holds `trades.draft` (Admin by default, granted per person). */}
            <Tabs.Panel value="view" pt="xs"><ViewTab request={viewRequest} tradable={can('trades.draft')} /></Tabs.Panel>
            <Tabs.Panel value="security" pt="md"><SecurityTab onOpenPortfolio={openPortfolio} /></Tabs.Panel>
            <Tabs.Panel value="trades" pt="md">
              {shownTab === 'trades' && <PortfolioTradesTab
                onOpen={(t) => openPortfolio(t.portfolio, can('trades.draft') ? t.id : null)} onOpenRun={openRun} />}
            </Tabs.Panel>
            {/* A client-data read: only while its tab is open (DLP read log / rate gate, C-395). */}
            <Tabs.Panel value="nocost" pt="md">
              {shownTab === 'nocost' && <PortfolioNoCostTab onOpenPortfolio={openPortfolio} />}
            </Tabs.Panel>
          </Tabs>
          </TabDirtyCtx.Provider>
        </Stack>
      </Container>
    </RequireAccess>
  );
}

// ── Upload ────────────────────────────────────────────────────────────────────

function UploadTab({ onOpenPortfolio }: { onOpenPortfolio: (ref: string) => void }) {
  const qc = useQueryClient();
  const [bankId, setBankId] = useState<string | null>(null);
  const [asOf, setAsOf] = useState(localTodayISO());
  const [file, setFile] = useState<File | null>(null);
  const [report, setReport] = useState<UploadReport | null>(null);
  const [previewedFor, setPreviewedFor] = useState<string | null>(null);
  const [saved, setSaved] = useState<{ r: UploadReport; bankId: string } | null>(null);
  const [busy, setBusy] = useState<'preview' | 'save' | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [rollbackId, setRollbackId] = useState<string | null>(null);
  const uploadsQ = useQuery({ queryKey: ['portfolios', 'uploads'], queryFn: () => portfoliosApi.uploads() });
  // The bank picker mirrors Account detail's: the mnemonic, "MNE (Name)" in the list (0112).
  const banksQ = useQuery({ queryKey: ['banks'], queryFn: () => banksApi.list(), staleTime: 60_000 });
  // 0114: only a bank with a file layout can be chosen; the others are listed greyed, with why (§3h).
  const layoutsQ = useQuery({ queryKey: ['portfolio-layouts'], queryFn: () => portfolioLayoutsApi.banks() });
  const withLayout = new Set((layoutsQ.data ?? []).filter((b) => b.has_layout).map((b) => b.bank_id));
  const bankOptions = (banksQ.data ?? []).map((b) => ({ value: b.id, label: b.mnemonic, disabled: !withLayout.has(b.id) }));
  const bankNameById = Object.fromEntries((banksQ.data ?? []).map((b) => [b.id, b.name]));
  const bankFilter: OptionsFilter = ({ options, search }) => {
    const q = search.trim().toLowerCase();
    return (options as ComboboxItem[]).filter((o) =>
      !q || o.label.toLowerCase().includes(q) || (bankNameById[o.value] ?? '').toLowerCase().includes(q));
  };

  const today = localTodayISO();
  const keyOf = (f: File, d: string, b: string) => `${f.name}|${f.size}|${d}|${b}`;
  const runPreview = async (f: File, d: string, b: string) => {
    setBusy('preview'); setReport(null); setSaved(null);
    try {
      setReport(await portfoliosApi.preview(f, d, b));
      setPreviewedFor(keyOf(f, d, b));
    } catch (e) {
      notifications.show({ color: 'red', title: 'Preview failed', message: errMsg(e) });
    } finally { setBusy(null); }
  };
  const onSelect = (f: File) => { setFile(f); if (asOf && bankId) void runPreview(f, asOf, bankId); };
  const onDate = (d: string) => { setAsOf(d); if (file && bankId && d && d <= today) void runPreview(file, d, bankId); };
  const onBank = (b: string | null) => {
    setBankId(b);
    if (b && file && asOf && asOf <= today) void runPreview(file, asOf, b); else setReport(null);
  };

  const stale = !!report && !!file && previewedFor !== keyOf(file, asOf, bankId ?? '');
  const saveReason =
    !bankId ? 'Choose the bank whose file this is first'
    : !file ? 'Choose the positions file first'
    : !asOf ? 'Enter the positions date first'
    : asOf > today ? 'The positions date cannot be in the future'
    : !report ? 'Wait for the preview'
    : stale ? 'The date or file changed since the preview: it is being read again'
    : !report.can_save ? 'The preview found problems that stop the save (see above)'
    : null;

  const doSave = async () => {
    if (!file || !report || !bankId) return;
    setConfirmOpen(false); setBusy('save');
    try {
      const r = await portfoliosApi.save(file, asOf, bankId, report.replaces.length > 0);
      setSaved({ r, bankId }); setReport(null); setFile(null);
      await qc.invalidateQueries({ queryKey: ['portfolios'] });
      await qc.invalidateQueries({ queryKey: ['securities'] });
      notifications.show({ color: 'green', title: 'Positions saved',
        message: `${r.portfolios.length} ${r.bank_mnemonic ?? ''} portfolio${r.portfolios.length === 1 ? '' : 's'} for ${r.as_of}.` });
    } catch (e) {
      const detail = e instanceof ApiError && e.status === 409
        ? `${e.detail} Preview again to see which.` : errMsg(e);
      notifications.show({ color: 'red', title: 'Positions not saved', message: detail });
    } finally { setBusy(null); }
  };
  const onSaveClick = () => { if (report && report.replaces.length > 0) setConfirmOpen(true); else void doSave(); };

  return (
    <Stack gap="lg">
      <Card withBorder>
        <Stack gap="sm">
          <Text fw={600}>1 · Bank, positions date and file</Text>
          <Text size="xs" c="dimmed">
            One workbook with every client&apos;s positions on one date, from one bank: its valuation layout with the
            portfolio number in the first column (&ldquo;Portfolio&rdquo;). The sheet carries neither the bank nor the
            date, so choose them here. A portfolio is the bank and its number: the same number at two banks is two portfolios.
          </Text>
          <Group align="flex-start">
            <Select label="Bank" required w={200} searchable clearable
              placeholder={banksQ.isLoading ? 'Loading banks…' : 'Choose a bank'}
              data={bankOptions} filter={bankFilter} value={bankId} onChange={onBank}
              nothingFoundMessage="No bank: Admin adds banks in Admin → Banks"
              renderOption={({ option }) => (
                <Text size="sm"><b>{option.label}</b> ({bankNameById[option.value] ?? ''})
                  {!withLayout.has(option.value) && <Text span size="xs" c="dimmed"> · no file layout yet: Setup upload tab</Text>}
                </Text>
              )} />
            <TextInput label="Bank name" readOnly variant="filled" tabIndex={-1} w={260}
              value={bankId ? bankNameById[bankId] ?? '' : ''} />
            <TextInput label="Positions date" type="date" required w={200} max={today}
              value={asOf} onChange={(e) => onDate(e.currentTarget.value)}
              error={asOf > today ? 'Cannot be in the future' : undefined} />
          </Group>
          <XlsxDropzone hint="One Excel file: the custodian's positions file for one portfolio, as exported by the bank."
            onSelect={onSelect} loading={busy === 'preview'}
            onReject={() => notifications.show({ color: 'red', title: 'Not accepted', message: 'Please choose a single .xlsx file.' })} />
          {file && <Text size="sm">Chosen: <b>{file.name}</b></Text>}
        </Stack>
      </Card>

      {saved && <SavedSummary r={saved.r} bankId={saved.bankId} onOpenPortfolio={onOpenPortfolio} />}

      {report && !stale && (
        <Card withBorder>
          <Stack gap="md">
            <Text fw={600}>2 · Preview — what this file holds and what saving does</Text>
            <ReportBody r={report} />
            <Group justify="flex-end">
              <Button variant="default" onClick={() => { setReport(null); setFile(null); }}>Cancel</Button>
              <DisabledTooltipButton disabledReason={saveReason} loading={busy === 'save'}
                color={report.replaces.length > 0 ? 'red' : undefined}
                leftSection={<IconCheck size={16} />} onClick={onSaveClick}>
                {report.replaces.length > 0 ? 'Replace and save' : 'Save positions'}
              </DisabledTooltipButton>
            </Group>
          </Stack>
        </Card>
      )}
      {busy === 'preview' && <Loader size="sm" />}

      <Modal opened={confirmOpen} onClose={() => setConfirmOpen(false)} title="Replace stored positions?">
        <Stack>
          <Text size="sm">
            Positions for <b>{asOf}</b> are already stored for{' '}
            <b>{report?.replaces.length} {report?.bank_mnemonic ?? ''} portfolio{report?.replaces.length === 1 ? '' : 's'}</b>
            {' '}({report?.replaces.join(', ')}). Saving replaces them with this file&apos;s lines. Other
            portfolios, other banks and other dates are not touched.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button color="red" onClick={doSave} loading={busy === 'save'}>Replace and save</Button>
          </Group>
        </Stack>
      </Modal>

      <Card withBorder>
        <Stack gap="xs">
          <Text fw={600}>Recent uploads</Text>
          {uploadsQ.isLoading ? <Loader size="sm" /> : (uploadsQ.data ?? []).length === 0 ? (
            <Text size="sm" c="dimmed">No positions uploaded yet.</Text>
          ) : (
            <Table striped withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Positions date</Table.Th><Table.Th>Bank</Table.Th><Table.Th>File</Table.Th>
                  <Table.Th ta="right">Portfolios</Table.Th><Table.Th ta="right">Positions</Table.Th>
                  <Table.Th ta="right">Replaced</Table.Th><Table.Th ta="right">Securities added</Table.Th>
                  <Table.Th>Uploaded</Table.Th><Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(uploadsQ.data ?? []).map((u) => (
                  <Table.Tr key={u.id} c={u.rolled_back_at ? 'dimmed' : undefined}>
                    <Table.Td>{u.as_of}</Table.Td>
                    <Table.Td>{u.bank_mnemonic ?? <Text span size="sm" c="dimmed">Not recorded</Text>}</Table.Td>
                    <Table.Td>{u.file_name}</Table.Td>
                    <Table.Td ta="right">{u.portfolios}</Table.Td><Table.Td ta="right">{u.positions}</Table.Td>
                    <Table.Td ta="right">{u.replaced_portfolios}</Table.Td><Table.Td ta="right">{u.securities_added}</Table.Td>
                    <Table.Td>
                      <Text size="sm">{new Date(u.uploaded_at).toLocaleString()}</Text>
                      <Text size="xs" c="dimmed">{u.uploaded_by ?? '—'}</Text>
                    </Table.Td>
                    <Table.Td>
                      {u.rolled_back_at ? (
                        <Tooltip withArrow label={`Rolled back ${new Date(u.rolled_back_at).toLocaleString()}${u.rolled_back_by ? ` by ${u.rolled_back_by}` : ''}`}>
                          <Badge color="gray" variant="light">Rolled back</Badge>
                        </Tooltip>
                      ) : (
                        <Button size="xs" variant="subtle" color="red" leftSection={<IconArrowBackUp size={14} />}
                          onClick={() => setRollbackId(u.id)}>
                          Roll back
                        </Button>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Stack>
      </Card>
      <RollbackModal uploadId={rollbackId} onClose={() => setRollbackId(null)} />
    </Stack>
  );
}

/** Roll an upload back (Pedro 2026-10-18, option A): the preview says exactly what goes and
 *  what comes back; the red button is the commit (§3g Pattern B, its own confirm). */
function RollbackModal({ uploadId, onClose }: { uploadId: string | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const planQ = useQuery({
    queryKey: ['portfolios', 'rollback-plan', uploadId],
    queryFn: () => portfoliosApi.rollbackPlan(uploadId!),
    enabled: !!uploadId,
    staleTime: 0,
  });
  const p = planQ.data;
  const doRollback = async () => {
    if (!uploadId) return;
    setBusy(true);
    try {
      const r = await portfoliosApi.rollback(uploadId);
      await qc.invalidateQueries({ queryKey: ['portfolios'] });
      await qc.invalidateQueries({ queryKey: ['securities'] });
      notifications.show({ color: 'green', title: 'Upload rolled back',
        message: `${r.file_name} (${r.as_of}): ${r.lines} lines removed, ${r.securities_removed.length} securit${r.securities_removed.length === 1 ? 'y' : 'ies'} removed.` });
      onClose();
    } catch (e) {
      notifications.show({ color: 'red', title: 'Not rolled back', message: errMsg(e) });
    } finally { setBusy(false); }
  };
  return (
    <Modal opened={!!uploadId} onClose={onClose} size="lg" title="Roll back this upload?">
      {planQ.isLoading && <Loader size="sm" />}
      {planQ.error && <Alert color="red">{errMsg(planQ.error)}</Alert>}
      {p && (
        <Stack gap="sm">
          <Text size="sm">
            <b>{p.file_name}</b>, positions date <b>{p.as_of}</b>, uploaded {new Date(p.uploaded_at).toLocaleString()}.
            Its {p.lines} lines are removed as if it had never been uploaded. The upload stays in the list, marked
            &ldquo;Rolled back&rdquo;. This cannot be undone: to restore it, upload the file again.
          </Text>
          <Table withTableBorder fz="sm">
            <Table.Thead><Table.Tr><Table.Th>Portfolio</Table.Th><Table.Th>Date</Table.Th><Table.Th>After the rollback</Table.Th></Table.Tr></Table.Thead>
            <Table.Tbody>
              {p.portfolios.map((x) => (
                <Table.Tr key={x.portfolio_number}>
                  <Table.Td><b>{x.portfolio_key}</b> <Text span size="xs" c="dimmed">{x.portfolio_number}</Text></Table.Td>
                  <Table.Td>{x.as_of}</Table.Td>
                  <Table.Td>{x.outcome}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          {p.securities_removed.length > 0 && (
            <Text size="sm">
              <b>Removed from the Securities Master</b> (added by this upload, held nowhere else, untouched since):{' '}
              {p.securities_removed.map((s) => `${s.name} (${s.isin} ${s.currency})`).join('; ')}.
            </Text>
          )}
          {p.securities_kept.length > 0 && (
            <Text size="sm">
              <b>Kept in the Securities Master:</b>{' '}
              {p.securities_kept.map((s) => `${s.name} (${s.isin} ${s.currency}): ${s.reason}`).join('; ')}.
            </Text>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>Cancel</Button>
            <Button color="red" loading={busy} onClick={doRollback} leftSection={<IconArrowBackUp size={16} />}>Roll back</Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}

function NewSecuritiesAlert({ r, saved }: { r: UploadReport; saved: boolean }) {
  if (r.new_securities.length === 0) return null;
  const n = r.new_securities.length;
  return (
    <Alert color="yellow" icon={<IconAlertTriangle size={18} />}
      title={`${n} securit${n === 1 ? 'y is' : 'ies are'} not in the Securities Master`}>
      <Stack gap="xs">
        <Text size="sm">
          {saved ? 'They were kept' : 'Saving keeps them'} in their portfolios under <b>{NON_CLASSIFIED}</b> and{' '}
          {saved ? 'added' : 'adds'} them to the Securities Master, not classified and not approved. Classify
          (and approve) them in{' '}
          <Anchor component={Link} to="/admin/securities?nonClassified=1">Admin → Securities</Anchor>; every stored
          date then shows them under their classification.
        </Text>
        <Table withTableBorder fz="sm">
          <Table.Thead>
            <Table.Tr><Table.Th>ISIN</Table.Th><Table.Th>Currency</Table.Th><Table.Th>Name in the file</Table.Th><Table.Th>Portfolios</Table.Th></Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {r.new_securities.map((s) => (
              <Table.Tr key={`${s.isin}|${s.currency}`}>
                <Table.Td>{s.isin}</Table.Td>
                <Table.Td>
                  {s.currency}
                  {s.in_master_other_currencies.length > 0 && (
                    <Tooltip label={`The master has this ISIN in ${s.in_master_other_currencies.join(', ')} only`}>
                      <Badge ml={6} size="xs" variant="outline" color="gray">other currency in master</Badge>
                    </Tooltip>
                  )}
                </Table.Td>
                <Table.Td>{s.name}</Table.Td>
                <Table.Td>{s.portfolios.join(', ')}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Stack>
    </Alert>
  );
}

function ReportBody({ r }: { r: UploadReport }) {
  return (
    <Stack gap="md">
      {r.blocking.length > 0 && (
        <Alert color="red" title={`${r.blocking.length} problem${r.blocking.length === 1 ? '' : 's'} stop the save`}>
          <Stack gap={2}>
            {r.blocking.slice(0, 50).map((b, i) => (
              <Text key={i} size="xs">
                {b.row ? `Row ${b.row}` : 'File'}{b.portfolio_key ? ` · portfolio ${b.portfolio_key}` : ''}: {b.reason}
              </Text>
            ))}
            {r.blocking.length > 50 && <Text size="xs">… and {r.blocking.length - 50} more</Text>}
            <Text size="xs" c="dimmed">Nothing can be saved until these are fixed in the file.</Text>
          </Stack>
        </Alert>
      )}
      <Text size="sm">
        <b>{r.portfolios.length}</b> portfolio{r.portfolios.length === 1 ? '' : 's'} · <b>{r.positions}</b> position{r.positions === 1 ? '' : 's'} ·{' '}
        <b>{r.cash_lines}</b> cash line{r.cash_lines === 1 ? '' : 's'} ·{' '}
        {(r.loan_lines ?? 0) > 0 && <><b>{r.loan_lines}</b> loan line{r.loan_lines === 1 ? '' : 's'} ·{' '}</>}
        {r.bank_mnemonic && <>bank <b>{r.bank_mnemonic}</b> · </>}positions date <b>{r.as_of}</b> · {r.file_name}
      </Text>
      {r.layout_note && <Text size="xs" c="dimmed">Read with {r.layout_note}.</Text>}
      <NewSecuritiesAlert r={r} saved={false} />
      {r.without_isin.length > 0 && (
        <Alert color="yellow" icon={<IconAlertTriangle size={18} />}
          title={`${r.without_isin.length} line${r.without_isin.length === 1 ? ' has' : 's have'} no ISIN`}>
          <Stack gap={2}>
            {r.without_isin.map((w, i) => (
              <Text key={i} size="xs">Row {w.row} · portfolio {w.portfolio_key}: {w.reason}</Text>
            ))}
          </Stack>
        </Alert>
      )}
      {(r.unreadable ?? []).length > 0 && (
        <Alert color="yellow" icon={<IconAlertTriangle size={18} />}
          title={`${r.unreadable!.length} line${r.unreadable!.length === 1 ? ' has' : 's have'} a value that could not be read`}>
          <Stack gap={2}>
            <Text size="xs">
              Informational columns only (none of them changes a figure): the value is kept empty. Saving is not blocked.
            </Text>
            {r.unreadable!.slice(0, 50).map((w, i) => (
              <Text key={i} size="xs">Row {w.row} · portfolio {w.portfolio_key}: {w.reason}</Text>
            ))}
            {r.unreadable!.length > 50 && <Text size="xs">… and {r.unreadable!.length - 50} more</Text>}
          </Stack>
        </Alert>
      )}
      {r.portfolios.length > 0 && (
        <Table striped withTableBorder fz="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Portfolio</Table.Th><Table.Th ta="right">Positions</Table.Th><Table.Th ta="right">Cash lines</Table.Th>
              <Table.Th ta="right">Loan lines</Table.Th>
              <Table.Th ta="right" title="In the portfolio's reference currency (Ref Ccy): the file's Net value (clean) plus its Accrued interest, both as the file gives them in that currency">Value (portfolio's Ref Ccy)</Table.Th>
              <Table.Th ta="right" title="The same value in EUR, at the file's own rates; - when the file gives no rate for that currency">Value (Ref Ccy EUR)</Table.Th>
              <Table.Th ta="right">Not in master</Table.Th><Table.Th>For this date</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {r.portfolios.map((p) => (
              <Table.Tr key={p.portfolio_number}>
                <Table.Td><b>{p.portfolio_key}</b> <Text span size="xs" c="dimmed">{p.portfolio_number}</Text></Table.Td>
                <Table.Td ta="right">{p.positions}</Table.Td><Table.Td ta="right">{p.cash_lines}</Table.Td>
                <Table.Td ta="right">{p.loan_lines || '—'}</Table.Td>
                {/* REF-HL (app-language pattern 20): a portfolio total in a currency other than EUR is yellow. */}
                <Table.Td ta="right" style={(p.ref_currency ?? 'EUR') !== 'EUR' ? { background: 'var(--mantine-color-yellow-2)', fontWeight: 600 } : undefined}
                  title={(p.ref_currency ?? 'EUR') !== 'EUR' ? `This value is in ${p.ref_currency}, not EUR.` : undefined}>
                  {eur(p.value_ref ?? p.value_eur)} {p.ref_currency ?? 'EUR'}
                </Table.Td>
                <Table.Td ta="right">{eur(p.value_eur)}</Table.Td>
                <Table.Td ta="right">{p.new_securities + p.without_isin || '—'}</Table.Td>
                <Table.Td>
                  {p.already_stored
                    ? <Badge color="red" variant="light">Replaces stored</Badge>
                    : <Badge color="gray" variant="light">New</Badge>}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  );
}

function SavedSummary({ r, bankId, onOpenPortfolio }: {
  r: UploadReport; bankId: string; onOpenPortfolio: (ref: string) => void;
}) {
  return (
    <Stack gap="md">
      <Alert color="green" icon={<IconCheck size={18} />}
        title={`Positions saved for ${r.as_of}${r.bank_mnemonic ? ` · ${r.bank_mnemonic}` : ''}`}>
        <Stack gap="xs">
          <Text size="sm">
            {r.portfolios.length} portfolio{r.portfolios.length === 1 ? '' : 's'} · {r.positions} position{r.positions === 1 ? '' : 's'} ·{' '}
            {r.securities_added} securit{r.securities_added === 1 ? 'y' : 'ies'} added to the Securities Master
            {r.replaced.length > 0 ? ` · replaced ${r.replaced.join(', ')}` : ''}.
          </Text>
          <Group gap="xs">
            <Text size="sm">View:</Text>
            {r.portfolios.map((p) => (
              <Button key={p.portfolio_number} size="xs" variant="light"
                onClick={() => onOpenPortfolio(`${bankId}:${p.portfolio_number}`)}>
                {p.portfolio_key}
              </Button>
            ))}
          </Group>
        </Stack>
      </Alert>
      <NewSecuritiesAlert r={r} saved />
    </Stack>
  );
}

// ── View: in PortfolioViewTab.tsx (one or several portfolios, HP-C) ─────────────

// ── View security: one security's line in each portfolio that holds it ─────────

function HeldSecurityPicker({ value, onChange }: { value: string | null; onChange: (id: string | null) => void }) {
  return (
    <EntityPicker<HeldSecurity>
      label="Security" placeholder="Type a name or an ISIN" entityNoun="held securities"
      value={value} onChange={onChange}
      search={(q) => portfoliosApi.heldSecurities(q)}
      getById={async (id) => {
        const d = await securitiesApi.get(id);
        return { id: d.id, isin: d.isin, currency: d.currency, name: d.description, portfolios: 0 };
      }}
      formatLabel={(r) => r.name}
      formatDetail={(r) => `${r.isin} ${r.currency}${r.portfolios ? ` · in ${r.portfolios} portfolio${r.portfolios === 1 ? '' : 's'}` : ''}`}
    />
  );
}

function SecurityTab({ onOpenPortfolio }: { onOpenPortfolio: (ref: string) => void }) {
  const [secId, setSecId] = useState<string | null>(null);
  const [asOf, setAsOf] = useState<string | undefined>(undefined);
  // SEC-LADDER: fallback rates chosen or typed for this view only ("bank:CCY" → choice).
  const [rateChoices, setRateChoices] = useState<Record<string, string>>({});
  const q = useQuery({
    queryKey: ['portfolios', 'by-security', secId, asOf ?? 'latest', JSON.stringify(rateChoices)],
    queryFn: () => portfoliosApi.bySecurity(secId!, asOf, rateChoices),
    enabled: !!secId,
    placeholderData: (prev) => prev,
  });
  const d = q.data;
  // A line converted to EUR with a substituted rate: its Value (EUR) cell says so (this view
  // has no FX-to-EUR column, so the rate shows on the value it produces).
  const subOf = (ln: { ref: string; ref_currency?: string | null }) => (d?.rate_sources ?? [])
    .find((s) => ln.ref.startsWith(`${s.bank}:`) && s.currency === (ln.ref_currency ?? 'EUR'));
  return (
    <Stack gap="lg">
      <Card withBorder style={{ overflow: 'visible' }}>
        <Group align="flex-end">
          <div style={{ width: 420 }}>
            <HeldSecurityPicker value={secId} onChange={(id) => { setSecId(id); setAsOf(undefined); setRateChoices({}); }} />
          </div>
          {d && (
            <Select label="Positions date" w={150} allowDeselect={false}
              data={d.dates} value={d.as_of} onChange={(x) => { if (x) { setAsOf(x); setRateChoices({}); } }} />
          )}
        </Group>
        <Text size="xs" c="dimmed" mt="xs">Only securities held in at least one stored portfolio are listed.</Text>
      </Card>
      {q.isLoading && <Loader size="sm" />}
      {q.error && <Alert color="red" title="Security not shown">{errMsg(q.error)}</Alert>}
      {d && (
        <Card withBorder>
          <Stack gap="md">
            <div>
              <Title order={3}>{d.security.name}</Title>
              <Text size="sm" c="dimmed">
                {d.security.isin} {d.security.currency} · held in {d.portfolios} portfolio{d.portfolios === 1 ? '' : 's'} on {d.as_of} ·
                total {num(d.total_quantity)} · <b>{eur(d.total_value_eur)} EUR</b>
              </Text>
            </div>
            <RateNotices missing={d.missing_rates ?? []} sources={d.rate_sources ?? []} leftOut={d.left_out ?? []}
              shown="EUR" onChoose={(key, choice) => setRateChoices((s) => {
                const x = { ...s };
                if (choice) x[key] = choice; else delete x[key];
                return x;
              })} />
            {/* COST-FIX (Pedro 2026-10-27, View security smoke: "please use the "cost=o" banner here as
                well"): the same yellow banner as View portfolio, naming the portfolios. */}
            {(() => {
              const noCost = d.lines.filter((ln) => costUnknown(ln));
              return noCost.length > 0 && (
                <Alert color="yellow" py={6} icon={<IconAlertTriangle size={16} />}>
                  <Text size="sm">
                    <b>{noCost.length} portfolio{noCost.length === 1 ? ' holds' : 's hold'} this security with no cost from the
                    custodian</b> (Average purchase price and Total cost 0), so {noCost.length === 1 ? 'its' : 'their'} cost and
                    P&amp;L are not reliable: {noCost.slice(0, 5).map((ln) => ln.label).join(', ')}
                    {noCost.length > 5 ? ` and ${noCost.length - 5} more` : ''}. They are shown as the custodian gives them. See{' '}
                    <Anchor component={Link} size="sm" to="/admin/portfolios?tab=nocost">Portfolios → No cost</Anchor> for every
                    such line across all portfolios.
                  </Text>
                </Alert>
              );
            })()}
            {/* COST-SRC B smoke (Pedro 2026-10-27): "you can keep a banner, but now cyan, saying that the
                cost was user input in the app". */}
            {(() => {
              const entered = d.lines.filter((ln) => ln.cost_entered);
              return entered.length > 0 && (
                <Alert color="cyan" py={6} icon={<IconInfoCircle size={16} />}>
                  <Text size="sm">
                    <b>{entered.length} portfolio{entered.length === 1 ? ' holds' : 's hold'} this security with a cost entered in
                    the app</b>, not given by the custodian: {entered.slice(0, 5).map((ln) => ln.label).join(', ')}
                    {entered.length > 5 ? ` and ${entered.length - 5} more` : ''}. Their Avg purchase price and Total P&amp;L use
                    it (shaded cyan; hover for where it comes from). See or change it in{' '}
                    <Anchor component={Link} size="sm" to="/admin/portfolios?tab=nocost">Portfolios → No cost</Anchor>.
                  </Text>
                </Alert>
              );
            })()}
            <Table.ScrollContainer minWidth={1000}>
              <Table withTableBorder striped fz="sm" verticalSpacing={4}>
                <Table.Thead>
                  <Table.Tr>
                    {/* CCY-LBL (Pedro 2026-10-23): every amount says Sec Ccy or Ref Ccy. This view shows EUR. */}
                    <Table.Th>Portfolio</Table.Th><Table.Th ta="right">Quantity</Table.Th>
                    <Table.Th ta="right" title="In the security's currency (Sec Ccy); a bond's price is per 100 nominal">Price (Sec Ccy)</Table.Th>
                    <Table.Th ta="right" title="In the security's currency (Sec Ccy), accrued interest included (the file's Valuation pos. curr.)">Value (Sec Ccy)</Table.Th>
                    <Table.Th ta="right" title="EUR per 1 unit of the security's currency: the custodian's exchange rate">FX (Sec Ccy → Ref Ccy EUR)</Table.Th>
                    <Table.Th ta="right" title="In EUR: the file's Net value (clean) plus its Accrued interest, converted from the portfolio's reference currency at its custodian's rate">Value (Ref Ccy EUR)</Table.Th>
                    <Table.Th ta="right" title="A share of the portfolio's gross value (loans left out)">% of portfolio</Table.Th>
                    <Table.Th ta="right" title="Every line but the loans, accrued interest included, in EUR">Portfolio gross value (Ref Ccy EUR)</Table.Th>
                    <Table.Th ta="right" title="The file's Average purchase price, in the security's currency (Sec Ccy). Shaded yellow: the custodian gives no cost for that line (0), so its cost and P&L are not reliable">Avg purchase price (Sec Ccy)</Table.Th>
                    <Table.Th ta="right" title="In EUR: the file's Total P&L = Net value (clean) − Total cost, both in the portfolio's reference currency, converted to EUR">Total P&amp;L (Ref Ccy EUR)</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {d.lines.map((ln) => (
                    <Table.Tr key={ln.ref || ln.portfolio_number}>
                      <Table.Td>
                        <Tooltip label="Open this portfolio in View portfolio" withArrow>
                          <Anchor size="sm" fw={600} onClick={() => onOpenPortfolio(ln.ref)}>{ln.label}</Anchor>
                        </Tooltip>
                      </Table.Td>
                      <Table.Td ta="right">{num(ln.quantity)}</Table.Td>
                      <Table.Td ta="right">{num(ln.price)}</Table.Td>
                      <Table.Td ta="right">{eur(ln.value_pos)}</Table.Td>
                      <Table.Td ta="right">{num(ln.fx, 8)}</Table.Td>
                      <Table.Td ta="right" title={subOf(ln) ? sourceWhy(subOf(ln)!, 'This value') : undefined}
                        style={subOf(ln) ? { background: 'var(--mantine-color-yellow-light)' } : undefined}>{eur(ln.value_eur)}</Table.Td>
                      <Table.Td ta="right">{pct(ln.pct_of_portfolio)}</Table.Td>
                      <Table.Td ta="right">{eur(ln.portfolio_value_eur)}</Table.Td>
                      {/* C-411 + Pedro 2026-10-27: as the custodian gives it (0 when it has no cost), marked. */}
                      <Table.Td ta="right" title={ln.cost_entered ? `Cost entered in the app (the custodian gives none): ${ln.cost_note ?? ''}`
                        : costUnknown(ln) ? 'No cost from the custodian: cost and P&L are not reliable (see the No cost tab)' : undefined}
                        style={ln.cost_entered ? { background: 'var(--mantine-color-cyan-light)' }
                          : costUnknown(ln) ? { background: 'var(--mantine-color-yellow-light)' } : undefined}>{num(ln.avg_price)}</Table.Td>
                      {/* COST-SRC B re-smoke (2026-10-27): Total P&L uses the entered cost too, so cyan as the banner says. */}
                      <Table.Td ta="right" title={ln.cost_entered ? `Net value − the cost entered in the app: ${ln.cost_note ?? ''}` : undefined}
                        style={ln.cost_entered ? { background: 'var(--mantine-color-cyan-light)' } : undefined}>{eur(ln.total_pnl)}</Table.Td>
                    </Table.Tr>
                  ))}
                  <Table.Tr>
                    <Table.Td><Text fw={700} size="sm">Total</Text></Table.Td>
                    <Table.Td ta="right"><Text fw={700} size="sm">{num(d.total_quantity)}</Text></Table.Td>
                    <Table.Td /><Table.Td /><Table.Td />
                    <Table.Td ta="right"><Text fw={700} size="sm">{eur(d.total_value_eur)}</Text></Table.Td>
                    <Table.Td /><Table.Td /><Table.Td /><Table.Td />
                  </Table.Tr>
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Stack>
        </Card>
      )}
    </Stack>
  );
}
